/**
 * Potential Trades lead filters (2026-09-09 owner ask).
 *
 * A friend's lead lists run to thousands of rows, and the only way to narrow
 * them was a region pill row of the page's own. This is the Dex page's filter
 * vocabulary applied to a trade lead instead: the same Region chips, the same
 * Type chips, the same Class row, the same search grammar and the same
 * Dex / Name sort. Every definition is REUSED from dexGridFilter rather than
 * restated, so a region or class added there shows up here without a second
 * edit, and the two surfaces can never disagree about what "Kanto" means.
 *
 * Pure and framework-free (run with `npx tsx client/src/lib/tradeLeadFilter.test.ts`).
 *
 * Only the IDENTITY axes live here: which species. What is true of the two
 * trainers (dex variant, base stage, lucky friend, wish list) stays in
 * useTradeHubData, layered on top of this.
 */
import { regionForId, typesFor, type PokeType } from "./types";
import { speciesName } from "../data/speciesNames";
import { matchesSpeciesClasses, type SpeciesClass } from "./speciesClass";
import {
  matchesSearch,
  normalizeSort,
  type RegionFilter,
  type SortOrder,
} from "./dexGridFilter";
import { leadFormKind } from "./leadFormKind";

export interface TradeLeadFilters {
  region: RegionFilter;
  /** Empty = any type. A dual-typed species matches on EITHER type. */
  types: PokeType[];
  /** Empty = any class. Multi-select, as on the Dex page. */
  classes: SpeciesClass[];
  /** Dex numbers exact, names substring, commas for a list. */
  search: string;
  sort: SortOrder;
  /**
   * Hide form-specific leads by kind (2026-09-19): a friend missing every
   * Pikachu costume floods the list with leads most trainers cannot fill.
   * Species-level leads (no form) are never hidden by these.
   */
  hideCostume: boolean;
  hideRegional: boolean;
}

export const DEFAULT_TRADE_LEAD_FILTERS: TradeLeadFilters = {
  region: "all",
  types: [],
  classes: [],
  search: "",
  sort: "dex-asc",
  hideCostume: false,
  hideRegional: false,
};

/** Which axes are narrowing (or reordering), for the menu's dots and count. */
export function tradeLeadActiveAxes(f: TradeLeadFilters) {
  return {
    search: f.search.trim() !== "",
    region: f.region !== "all",
    type: f.types.length > 0,
    classes: f.classes.length > 0,
    sort: normalizeSort(f.sort) !== "dex-asc",
    forms: f.hideCostume || f.hideRegional,
  };
}

export function tradeLeadActiveCount(f: TradeLeadFilters): number {
  return Object.values(tradeLeadActiveAxes(f)).filter(Boolean).length;
}

/** Does this species pass the identity axes? */
export function matchesTradeLead(id: number, f: TradeLeadFilters): boolean {
  if (f.region !== "all" && (regionForId(id) ?? "unknown") !== f.region) return false;
  if (!matchesSearch(id, f.search)) return false;
  if (f.types.length > 0 && !typesFor(id).some((t) => f.types.includes(t))) return false;
  if (!matchesSpeciesClasses(f.classes, id)) return false;
  return true;
}

/** Does this lead's FORM pass the hide-costume / hide-regional switches? */
export function matchesTradeLeadForm(
  id: number,
  formId: string | null | undefined,
  f: TradeLeadFilters,
): boolean {
  if (!f.hideCostume && !f.hideRegional) return true;
  const kind = leadFormKind(id, formId);
  if (kind === "costume" && f.hideCostume) return false;
  if (kind === "regional" && f.hideRegional) return false;
  return true;
}

/**
 * The base order of a lead list. Stable, so a species that appears under two
 * variants keeps its incoming (server) order between the two rows, and so the
 * hub's priority layers (lucky-friend tiers, wish-list bubble) can be applied
 * on top without disturbing it.
 */
export function tradeLeadComparator(sort: SortOrder): (a: { id: number }, b: { id: number }) => number {
  switch (normalizeSort(sort)) {
    case "dex-desc":
      return (a, b) => b.id - a.id;
    case "name-asc":
      return (a, b) => speciesName(a.id).localeCompare(speciesName(b.id)) || a.id - b.id;
    case "name-desc":
      return (a, b) => speciesName(b.id).localeCompare(speciesName(a.id)) || b.id - a.id;
    case "dex-asc":
    default:
      return (a, b) => a.id - b.id;
  }
}
